import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

const ThankYouPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f0f2f5;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  padding: 15px;
  box-sizing: border-box;
`;

const ThankYouContainer = styled.div`
  background-color: white;
  padding: 20px 30px;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 700px;
  width: 100%;
`;

const CompanyLogo = styled.img`
  max-width: 280px;
  height: auto;
  margin-bottom: 12px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const Header = styled.h1`
  color: #28a745;
  font-size: 1.5rem;
  margin-bottom: 8px;
`;

const InfoText = styled.p`
  color: #555;
  font-size: 0.9rem;
  line-height: 1.3;
  margin-bottom: 6px;
`;

const ApplicationIDText = styled.p`
  color: #333;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  padding: 6px 12px;
  border: 1px dashed #ccc;
  border-radius: 6px;
  display: inline-block;
`;

const ButtonGroup = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  width: 100%;
`;

const ActionButton = styled.button`
  background: linear-gradient(145deg, #007bff, #0056b3);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    background: linear-gradient(145deg, #0069d9, #004a99);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

// Modal styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);
  width: 400px;
  max-width: 90%;
  position: relative;
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  color: #333;
  font-size: 1.4em;
  font-weight: 600;
  margin-bottom: 20px;
`;

const ModalClose = styled.button`
  position: absolute;
  right: 15px;
  top: 15px;
  background: none;
  border: none;
  font-size: 22px;
  cursor: pointer;
  color: #999;
  &:hover {
    color: #555;
  }
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  &:focus {
    border-color: #6cb288;
    outline: none;
    box-shadow: 0 0 0 2px rgba(108, 178, 136, 0.2);
  }
`;

const SendButton = styled.button`
  background-color: #6cb288;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  &:hover {
    background-color: #5a9e76;
  }
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.p<{ isError?: boolean }>`
  color: ${props => props.isError ? '#e74c3c' : '#27ae60'};
  font-size: 14px;
  margin-top: 15px;
  margin-bottom: 0;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
  margin: 15px 0;
`;

const ProgressBarFill = styled.div<{ isComplete?: boolean }>`
  height: 100%;
  background: linear-gradient(90deg, #28a745, #20c997);
  border-radius: 3px;
  transition: width 2s ease-in-out;
  width: ${props => props.isComplete ? '100%' : '0%'};
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #27ae60;
  font-size: 16px;
  font-weight: 500;
  margin-top: 15px;
  
  &::before {
    content: '✓';
    display: inline-block;
    width: 24px;
    height: 24px;
    background-color: #27ae60;
    color: white;
    border-radius: 50%;
    text-align: center;
    line-height: 24px;
    font-weight: bold;
  }
`;

const DashboardButton = styled(ActionButton)`
  background: linear-gradient(145deg, #6c757d, #545b62);
  grid-column: span 2;
  &:hover {
    background: linear-gradient(145deg, #5a6268, #474d52);
  }
`;

const ThankYouPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDownloadingApplication, setIsDownloadingApplication] = useState(false);
  const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  
  // Email Receipt Modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<{ message: string; isError: boolean } | null>(null);
  
  // Auto-send receipt state - only show prompt if not fresh payment
  const isFreshPayment = searchParams.get('payment') === 'success';
  const paymentMethod = searchParams.get('method'); // 'dd' or 'netbanking' for pending verification
  const isPendingVerification = paymentMethod === 'dd' || paymentMethod === 'netbanking';
  const [autoSendStatus, setAutoSendStatus] = useState<'idle' | 'prompt' | 'sending' | 'sent' | 'failed'>(
    isFreshPayment ? 'sending' : 'prompt'
  );
  const autoSendAttempted = useRef(false);
  
  // Auto-send payment receipt ONLY when redirected from fresh payment
  useEffect(() => {
    const autoSendReceipt = async () => {
      // Only auto-send if this is a fresh payment redirect (has ?payment=success)
      if (!applicationId || !isFreshPayment || autoSendAttempted.current) return;
      autoSendAttempted.current = true;
      
      try {
        setAutoSendStatus('sending');
        
        // First, fetch the application to get the applicant's email
        const appResponse = await fetch(`/api/applications/${applicationId}`, {
          method: 'GET',
          credentials: 'include',
        });
        
        if (!appResponse.ok) {
          console.error('[AutoSend] Failed to fetch application details');
          setAutoSendStatus('failed');
          return;
        }
        
        const appData = await appResponse.json();
        const applicantEmail = appData.application?.email || appData.email;
        const applicantName = appData.application?.formData?.fullName || 
                              appData.application?.formData?.nameInBlocks || 
                              appData.formData?.fullName || 
                              appData.formData?.nameInBlocks || 
                              'Applicant';
        
        if (!applicantEmail) {
          console.error('[AutoSend] No email found for application');
          setAutoSendStatus('failed');
          return;
        }

        // Notify admin dashboard of new payment (fire and forget)
        try {
          fetch('/api/admin/notify-payment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              applicationId,
              applicantName,
              amount: 5000
            })
          }).catch(() => {}); // Silently ignore errors
        } catch (e) {
          // Ignore notification errors
        }

        // Send SMS/WhatsApp payment confirmation (fire and forget)
        const mobileNumber = appData.application?.formData?.mobileNumber || 
                             appData.formData?.mobileNumber;
        if (mobileNumber) {
          try {
            fetch('/api/send-payment-confirmation', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                mobileNumber,
                applicantName,
                amount: '5,000',
                applicationId
              })
            }).catch(() => {}); // Silently ignore errors
          } catch (e) {
            // Ignore SMS notification errors
          }
        }
        
        // Send the receipt to the applicant's email
        const sendResponse = await fetch(`/api/application/${applicationId}/email-receipt`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ email: applicantEmail })
        });
        
        if (sendResponse.ok) {
          console.log(`[AutoSend] Payment receipt sent to ${applicantEmail}`);
          setAutoSendStatus('sent');
        } else {
          console.error('[AutoSend] Failed to send receipt');
          setAutoSendStatus('failed');
        }
      } catch (error) {
        console.error('[AutoSend] Error:', error);
        setAutoSendStatus('failed');
      }
    };
    
    // Delay auto-send slightly to ensure page is fully loaded
    if (isFreshPayment) {
      const timer = setTimeout(autoSendReceipt, 1000);
      return () => clearTimeout(timer);
    }
  }, [applicationId, isFreshPayment]);
  
  // Handle user confirmation to send receipt (when not fresh payment)
  const handleSendReceiptConfirm = async () => {
    if (!applicationId || autoSendAttempted.current) return;
    autoSendAttempted.current = true;
    
    try {
      setAutoSendStatus('sending');
      
      const appResponse = await fetch(`/api/applications/${applicationId}`, {
        method: 'GET',
        credentials: 'include',
      });
      
      if (!appResponse.ok) {
        setAutoSendStatus('failed');
        return;
      }
      
      const appData = await appResponse.json();
      const applicantEmail = appData.application?.email || appData.email;
      
      if (!applicantEmail) {
        setAutoSendStatus('failed');
        return;
      }
      
      const sendResponse = await fetch(`/api/application/${applicationId}/email-receipt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: applicantEmail })
      });
      
      if (sendResponse.ok) {
        setAutoSendStatus('sent');
      } else {
        setAutoSendStatus('failed');
      }
    } catch (error) {
      setAutoSendStatus('failed');
    }
  };
  
  const handleDeclineReceipt = () => {
    setAutoSendStatus('idle');
  };

  const handleDownloadApplication = async () => {
    if (!applicationId) {
      setDownloadError('Application ID is missing.');
      return;
    }
    setIsDownloadingApplication(true);
    setDownloadError(null);
    try {
      // Assuming your api util is set up for blob responses
      // You might need to adjust api.ts or use fetch directly if not
      const response = await fetch(`/api/application/${applicationId}/download-pdf`, {
        method: 'GET',
        headers: {
          // Add any necessary headers, like Authorization if required
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to download PDF. Server error.' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `application-${applicationId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download application PDF error:', error);
      setDownloadError(error.message || 'An unexpected error occurred while downloading the PDF.');
    }
    setIsDownloadingApplication(false);
  };

  const handleDownloadReceipt = async () => {
    if (!applicationId) {
      setDownloadError('Application ID is missing for receipt.');
      return;
    }
    setIsDownloadingReceipt(true);
    setDownloadError(null);
    try {
      const response = await fetch(`/api/application/${applicationId}/payment-receipt-pdf`, {
        method: 'GET',
        headers: {
          // Add any necessary headers, like Authorization if required
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to download payment receipt. Server error.' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment-receipt-${applicationId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download payment receipt PDF error:', error);
      setDownloadError(error.message || 'An unexpected error occurred while downloading the payment receipt.');
    }
    setIsDownloadingReceipt(false);
  };

  const handleGoToDashboard = () => {
    navigate('/user-dashboard');
  };
  
  // Email receipt handlers
  const handleOpenEmailModal = () => {
    setIsEmailModalOpen(true);
    setSendStatus(null);
    // Pre-fill email if available from application
  };
  
  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setEmail('');
    setSendStatus(null);
  };
  
  const handleSendReceiptByEmail = async () => {
    if (!applicationId) {
      setSendStatus({
        message: 'Application ID is missing for receipt.',
        isError: true
      });
      return;
    }
    
    if (!email || !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setSendStatus({
        message: 'Please enter a valid email address.',
        isError: true
      });
      return;
    }
    
    setIsSending(true);
    setSendStatus(null);
    
    try {
      const response = await fetch(`/api/application/${applicationId}/email-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to send receipt. Server error.' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setIsSending(false);
      setSendStatus({
        message: 'Payment confirmation PDF has been emailed successfully!',
        isError: false
      });
      
      // Auto-close modal after 3 seconds
      setTimeout(() => {
        handleCloseEmailModal();
        setEmail('');
      }, 3000);
      
    } catch (error: any) {
      console.error('Send receipt by email error:', error);
      setIsSending(false);
      setSendStatus({
        message: error.message || 'An unexpected error occurred while sending the receipt.',
        isError: true
      });
    }
  };

  return (
    <ThankYouPageWrapper>
      <ThankYouContainer>
        <CompanyLogo src="/assets/images/new_banner.png" alt="Company Banner" />
        <Header>{isPendingVerification ? 'Application Submitted!' : 'Thank You!'}</Header>
        {downloadError && <p style={{ color: 'red', marginBottom: '10px' }}>Error: {downloadError}</p>}
        
        {/* Pending Verification Notice for DD/NetBanking */}
        {isPendingVerification && (
          <div style={{ 
            backgroundColor: '#fef3c7', 
            border: '1px solid #f59e0b', 
            borderRadius: '6px', 
            padding: '10px 15px', 
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <img src="/assets/images/pending_verification.png" alt="Pending" style={{ width: '32px', height: '32px' }} />
            <div style={{ textAlign: 'left' }}>
              <div style={{ color: '#92400e', fontWeight: 600, fontSize: '0.9rem' }}>Payment Pending Verification</div>
              <div style={{ color: '#78350f', fontSize: '0.75rem' }}>
                Your {paymentMethod === 'dd' ? 'DD' : 'NetBanking screenshot'} will be verified in 2-3 business days.
              </div>
            </div>
          </div>
        )}
        
        <InfoText>
          Your application has been successfully submitted.
        </InfoText>
        <InfoText>
          Your Application ID is:
        </InfoText>
        <ApplicationIDText>{applicationId || 'N/A'}</ApplicationIDText>
        
        {/* Prompt to send receipt (when visiting page directly, not from fresh payment) */}
        {autoSendStatus === 'prompt' && (
          <div style={{ 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #2196f3', 
            borderRadius: '6px', 
            padding: '8px 12px', 
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px'
          }}>
            <span style={{ color: '#1565c0', fontWeight: 500, fontSize: '0.8rem' }}>
              📧 Email payment receipt?
            </span>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button 
                onClick={handleSendReceiptConfirm}
                style={{ backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 12px', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem' }}
              >
                Yes
              </button>
              <button 
                onClick={handleDeclineReceipt}
                style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 12px', cursor: 'pointer', fontWeight: 500, fontSize: '0.75rem' }}
              >
                No
              </button>
            </div>
          </div>
        )}
        
        {/* Auto-send receipt status indicator */}
        {autoSendStatus === 'sending' && (
          <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '6px', padding: '6px 12px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>📧</span>
            <span style={{ color: '#856404', fontSize: '0.8rem' }}>Sending receipt...</span>
          </div>
        )}
        {autoSendStatus === 'sent' && (
          <div style={{ backgroundColor: '#d4edda', border: '1px solid #28a745', borderRadius: '6px', padding: '6px 12px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px' }}>✅</span>
            <span style={{ color: '#155724', fontSize: '0.8rem' }}>Receipt sent to your email!</span>
          </div>
        )}
        
        <ButtonGroup>
          {/* What's Next button - prominent position */}
          <ActionButton 
            onClick={() => navigate(`/next-steps/${applicationId}`)}
            style={{ background: 'linear-gradient(145deg, #ff6b35, #f54d00)' }}
          >
            What's Next? →
          </ActionButton>
          <ActionButton 
            onClick={handleOpenEmailModal} 
            disabled={isDownloadingReceipt || isSending}
            style={{ background: 'linear-gradient(145deg, #28a745, #218838)' }}
          >
            Email Payment Receipt
          </ActionButton>
          <ActionButton onClick={handleDownloadReceipt} disabled={isDownloadingReceipt}>
            {isDownloadingReceipt ? 'Downloading...' : 'Download Payment Receipt'}
          </ActionButton>
          <DashboardButton onClick={handleGoToDashboard}>
            Go to Dashboard
          </DashboardButton>
        </ButtonGroup>
      </ThankYouContainer>
      
      {/* Email Receipt Modal */}
      {isEmailModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <ModalClose onClick={handleCloseEmailModal}>&times;</ModalClose>
            <ModalTitle>Email Payment Receipt</ModalTitle>
            
            <EmailInput
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSending}
            />
            
            <SendButton 
              onClick={handleSendReceiptByEmail}
              disabled={isSending || !email || (sendStatus && !sendStatus.isError)}
            >
              {isSending ? 'Sending...' : 'Send Receipt'}
            </SendButton>
            
            {isSending && (
              <ProgressBarContainer>
                <ProgressBarFill isComplete={isSending} />
              </ProgressBarContainer>
            )}
            
            {sendStatus && !sendStatus.isError && (
              <SuccessMessage>
                {sendStatus.message}
              </SuccessMessage>
            )}
            
            {sendStatus && sendStatus.isError && (
              <StatusMessage isError={true}>
                {sendStatus.message}
              </StatusMessage>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </ThankYouPageWrapper>
  );
};

export default ThankYouPage;